class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");

    if (!form) {
      return;
    }

    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) {
      return;
    }

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search-item-button"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  getSearchResults(searchTerm) {
          console.log("GET SEARCH RESULTS:", searchTerm);

          const filterType = this.getSearchFilterType();
          const filterTypeQuery = this.getSearchFilterTypeQuery();

          /*
          * ==================================================
          * NORMALIZE SEARCH TERM
          * ==================================================
          */

          const typoMap = {
            "gren": "green",
            "grren": "green",
            "greeen": "green",
            "grean": "green",

            "wir": "wire",
            "wre": "wire",
            "wirre": "wire",

            "conector": "connector",
            "connecter": "connector",
            "connctor": "connector",

            "nutt": "nut",

            "groundng": "grounding",
            "groundig": "grounding"
          };

          const normalizeSearchTerm = (term) => {
            return term
              .trim()
              .toLowerCase()
              .replace(/\s+/g, " ")
              .split(" ")
              .map((word) => typoMap[word] || word)
              .join(" ");
          };

          const normalizedSearchTerm =
            normalizeSearchTerm(searchTerm);

          console.log(
            "NORMALIZED SEARCH:",
            normalizedSearchTerm
          );

          /*
          * ==================================================
          * CACHE KEY
          * ==================================================
          */

          const queryKey =
            normalizedSearchTerm.replace(/\s+/g, "-") +
            "-" +
            filterType +
            "-" +
            filterTypeQuery
              .replace(/\s+/g, "-")
              .toLowerCase();

          this.setLiveRegionLoadingState();

          /*
          * ==================================================
          * CACHE
          * ==================================================
          */

          if (this.cachedResults[queryKey]) {
            console.log(
              "USING CACHE:",
              queryKey
            );

            this.renderSearchResults(
              this.cachedResults[queryKey]
            );

            return;
          }

          /*
          * ==================================================
          * LIMIT
          * ==================================================
          *
          * Use at least 10 so synonym searches have
          * enough products to combine and rank.
          */

          const limit =
            Math.max(
              Number(this.dataset.limit) || 5,
              10
            );

          const limitScope =
            this.dataset.limitScope || "each";

          /*
          * ==================================================
          * SYNONYMS
          * ==================================================
          *
          * IMPORTANT:
          *
          * green
          * ->
          * green
          * green wire
          * green grounding wire
          * green ground wire
          * green wire connector
          * green wire nut
          * green connector
          *
          * This makes sure Green Wire Nut products
          * enter the result pool even when Shopify's
          * normal "green" search returns cable ties first.
          */

          const searchSynonyms = {

            /*
            * ----------------------------------------------
            * COLOR ONLY
            * ----------------------------------------------
            */

            "green": [
              "green wire",
              "green grounding wire",
              "green ground wire",
              "green wire connector",
              "green wire nut",
              "green connector",
              "green grounding connector"
            ],

            "yellow": [
              "yellow wire",
              "yellow grounding wire",
              "yellow ground wire",
              "yellow wire connector",
              "yellow wire nut",
              "yellow connector"
            ],

            "blue": [
              "blue wire",
              "blue grounding wire",
              "blue ground wire",
              "blue wire connector",
              "blue wire nut",
              "blue connector"
            ],

            "red": [
              "red wire",
              "red grounding wire",
              "red ground wire",
              "red wire connector",
              "red wire nut",
              "red connector"
            ],

            "orange": [
              "orange wire",
              "orange grounding wire",
              "orange ground wire",
              "orange wire connector",
              "orange wire nut",
              "orange connector"
            ],

            "black": [
              "black wire",
              "black grounding wire",
              "black ground wire",
              "black wire connector",
              "black wire nut",
              "black connector"
            ],

            "white": [
              "white wire",
              "white grounding wire",
              "white ground wire",
              "white wire connector",
              "white wire nut",
              "white connector"
            ],

            /*
            * ----------------------------------------------
            * GREEN SEARCHES
            * ----------------------------------------------
            */

            "green wire": [
              "green wire",
              "green grounding wire",
              "green ground wire",
              "green wire connector",
              "green wire nut"
            ],

            "green wire nut": [
              "green wire nut",
              "green wire connector",
              "green connector",
              "green grounding wire"
            ],

            "green wire connector": [
              "green wire connector",
              "green wire nut",
              "green connector",
              "green grounding wire"
            ],

            "green connector": [
              "green connector",
              "green wire connector",
              "green wire nut",
              "green grounding wire"
            ],

            /*
            * ----------------------------------------------
            * WIRE SEARCHES
            * ----------------------------------------------
            */

            "wire nut": [
              "wire nut",
              "wire connector",
              "wire cap",
              "twist on connector",
              "twist-on connector"
            ],

            "wire connector": [
              "wire connector",
              "wire nut",
              "wire cap",
              "twist on connector",
              "twist-on connector"
            ],

            "grounding wire": [
              "grounding wire",
              "ground wire",
              "green wire"
            ],

            "ground wire": [
              "ground wire",
              "grounding wire",
              "green wire"
            ]
          };

          /*
          * ==================================================
          * BUILD SEARCH QUERIES
          * ==================================================
          */

          const queries = [];

          const addQuery = (query) => {

            if (!query) {
              return;
            }

            query = query.trim();

            if (!query) {
              return;
            }

            const queryLower =
              query.toLowerCase();

            if (
              !queries.some(
                (item) =>
                  item.toLowerCase() ===
                  queryLower
              )
            ) {
              queries.push(query);
            }
          };

          /*
          * ==================================================
          * 1. ORIGINAL / NORMALIZED QUERY
          * ==================================================
          *
          * Always keep original query.
          */

          addQuery(normalizedSearchTerm);

          /*
          * ==================================================
          * 2. SYNONYM QUERIES
          * ==================================================
          */

          if (
            searchSynonyms[
              normalizedSearchTerm
            ]
          ) {

            searchSynonyms[
              normalizedSearchTerm
            ].forEach((query) => {
              addQuery(query);
            });

            console.log(
              "SYNONYM SEARCH:",
              normalizedSearchTerm,
              "=>",
              searchSynonyms[
                normalizedSearchTerm
              ]
            );
          }

          /*
          * ==================================================
          * 3. NUMBER + WORD NORMALIZATION
          * ==================================================
          *
          * 75 lb -> 75lb
          * 36 inch -> 36inch
          * 100 pack -> 100pack
          */

          // addQuery( 
          //   normalizedSearchTerm.replace(
          //     /(\d+)\s+([a-zA-Z]+)\b/g,
          //     "$1$2"
          //   )
          // );



          /*
        * ==================================================
        * 3. DIMENSION / UNIT ALIASES
        * ==================================================
        *
        * Examples:
        *
        * 36-inch
        * 36-in
        * 36 inch
        * 36 in
        * 36inch
        * 36in
        *
        * All forms will search Shopify.
        */

        const dimensionRegex =
          /(\d+(?:\.\d+)?)\s*-?\s*(inches|inch|in|feet|foot|ft|lbs|lb|pounds|pound|oz|ounces|ounce|mm|cm|m)\b/gi;

        let dimensionMatch;

        while (
          (dimensionMatch =
            dimensionRegex.exec(normalizedSearchTerm)) !== null
        ) {
          const number = dimensionMatch[1];
          const unit = dimensionMatch[2].toLowerCase();

          /*
          * ----------------------------------------------
          * INCH
          * ----------------------------------------------
          */

          if (
            unit === "inch" ||
            unit === "inches" ||
            unit === "in"
          ) {
            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number}-inch`
              )
            );

            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number}-in`
              )
            );

            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number} inch`
              )
            );

            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number} in`
              )
            );

            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number}inch`
              )
            );

            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number}in`
              )
            );
          }

          /*
          * ----------------------------------------------
          * FEET
          * ----------------------------------------------
          */

          if (
            unit === "foot" ||
            unit === "feet" ||
            unit === "ft"
          ) {
            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number}-ft`
              )
            );

            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number} ft`
              )
            );

            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number}ft`
              )
            );

            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number}-foot`
              )
            );

            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number} foot`
              )
            );
          }

          /*
          * ----------------------------------------------
          * POUND
          * ----------------------------------------------
          */

          if (
            unit === "lb" ||
            unit === "lbs" ||
            unit === "pound" ||
            unit === "pounds"
          ) {
            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number}-lb`
              )
            );

            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number} lb`
              )
            );

            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number}lb`
              )
            );

            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number}-lbs`
              )
            );

            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number} lbs`
              )
            );
          }

          /*
          * ----------------------------------------------
          * OUNCE
          * ----------------------------------------------
          */

          if (
            unit === "oz" ||
            unit === "ounce" ||
            unit === "ounces"
          ) {
            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number}-oz`
              )
            );

            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number} oz`
              )
            );

            addQuery(
              normalizedSearchTerm.replace(
                dimensionMatch[0],
                `${number}oz`
              )
            );
          }
        }

        /*
        * ==================================================
        * 4. NUMBER + WORD SEPARATION
        * ==================================================
        *
        * 36inch -> 36 inch
        * 36in    -> 36 in
        * 75lb    -> 75 lb
        *
        * Hyphenated values are also handled above.
        */

        addQuery(
          normalizedSearchTerm.replace(
            /(\d+(?:\.\d+)?)([a-zA-Z]+)\b/g,
            "$1 $2"
          )
        );

        console.log(
          "FINAL SEARCH QUERIES:",
          queries
        );

          /*
          * ==================================================
          * 4. NUMBER + WORD SEPARATION
          * ==================================================
          *
          * 75lb -> 75 lb
          * 36inch -> 36 inch
          */

          addQuery(
            normalizedSearchTerm.replace(
              /(\d+)([a-zA-Z]+)\b/g,
              "$1 $2"
            )
          );

          /*
          * IMPORTANT:
          *
          * DO NOT search individual words.
          *
          * "green wire"
          *
          * should NOT become:
          *
          * green
          * wire
          *
          * because that creates irrelevant results.
          */

          console.log(
            "FINAL SEARCH QUERIES:",
            queries
          );

          /*
          * ==================================================
          * SEARCH SHOPIFY
          * ==================================================
          */

          const searchPromises =
            queries.map(
              (currentSearchTerm, index) => {

                let query =
                  currentSearchTerm;

                /*
                * Apply filter if required.
                */

                if (filterTypeQuery) {
                  query =
                    `${filterType}:${filterTypeQuery} ${currentSearchTerm}`;
                }

                const url =
                  `${routes.predictive_search_url}` +
                  `?q=${encodeURIComponent(query)}` +
                  `&resources[type]=product` +
                  `&resources[limit_scope]=${limitScope}` +
                  `&resources[limit]=${limit}` +
                  `&options[prefix]=last` +
                  `&section_id=predictive-search` +
                  `&resources[options][fields]=title,product_type,tag,variants.title,variants.sku,body,vendor`;

                console.log(
                  `Predictive search [${index + 1}/${queries.length}]:`,
                  query
                );

                return fetch(url)
                  .then((response) => {

                    if (!response.ok) {
                      throw new Error(
                        `Predictive search error: ${response.status}`
                      );
                    }

                    return response.text();
                  })
                  .then((text) => {

                    const parsed =
                      new DOMParser()
                        .parseFromString(
                          text,
                          "text/html"
                        );

                    const section =
                      parsed.querySelector(
                        "#shopify-section-predictive-search"
                      );

                    if (!section) {

                      console.log(
                        "NO SECTION:",
                        currentSearchTerm
                      );

                      return null;
                    }

                    const productLinks =
                      section.querySelectorAll(
                        'a[href*="/products/"]'
                      );

                    if (!productLinks.length) {

                      console.log(
                        "NO PRODUCTS:",
                        currentSearchTerm
                      );

                      return null;
                    }

                    console.log(
                      "PRODUCTS FOUND:",
                      currentSearchTerm,
                      productLinks.length
                    );

                    return section;
                  })
                  .catch((error) => {

                    console.warn(
                      "Predictive search failed:",
                      currentSearchTerm,
                      error
                    );

                    return null;
                  });
              }
            );

          /*
          * ==================================================
          * COMBINE RESULTS
          * ==================================================
          */

          Promise.all(searchPromises)
            .then((sections) => {

              const validSections =
                sections.filter(Boolean);

              /*
              * ----------------------------------------------
              * NO RESULTS
              * ----------------------------------------------
              */

              if (!validSections.length) {

                console.log(
                  "NO SEARCH RESULTS FOR:",
                  normalizedSearchTerm
                );

                this.close();

                return;
              }

              /*
              * ----------------------------------------------
              * FIRST SECTION
              * ----------------------------------------------
              */

              const baseSection =
                validSections[0]
                  .cloneNode(true);

              /*
              * ----------------------------------------------
              * PRODUCT CONTAINER
              * ----------------------------------------------
              */

              const productContainer =
                baseSection.querySelector(
                  "ul, .predictive-search__results-list"
                );

              /*
              * ----------------------------------------------
              * FALLBACK
              * ----------------------------------------------
              */

              if (!productContainer) {

                const resultsMarkup =
                  baseSection.innerHTML;

                this.cachedResults[queryKey] =
                  resultsMarkup;

                this.renderSearchResults(
                  resultsMarkup
                );

                return;
              }

              /*
              * ==================================================
              * PRODUCT MAP
              * ==================================================
              */

              const productMap =
                new Map();

              validSections.forEach(
                (section, sectionIndex) => {

                  const otherContainer =
                    section.querySelector(
                      "ul, .predictive-search__results-list"
                    );

                  if (!otherContainer) {
                    return;
                  }

                  const productLinks =
                    Array.from(
                      otherContainer.querySelectorAll(
                        'a[href*="/products/"]'
                      )
                    );

                  productLinks.forEach(
                    (productLink, productIndex) => {

                      const productItem =
                        productLink.closest("li");

                      if (!productItem) {
                        return;
                      }

                      const productUrl =
                        productLink.href
                          .split("?")[0];

                      /*
                      * ----------------------------------------
                      * PRODUCT TEXT
                      * ----------------------------------------
                      */

                      const productText =
                        (
                          productItem.textContent ||
                          ""
                        )
                          .toLowerCase()
                          .replace(/\s+/g, " ")
                          .trim();

                      /*
                      * ----------------------------------------
                      * PRODUCT TITLE
                      * ----------------------------------------
                      */

                      const productTitle =
                        (
                          productLink.textContent ||
                          productLink.getAttribute(
                            "aria-label"
                          ) ||
                          ""
                        )
                          .toLowerCase()
                          .replace(/\s+/g, " ")
                          .trim();

                      /*
                      * ----------------------------------------
                      * INITIAL SCORE
                      * ----------------------------------------
                      */

                      let score = 0;

                      /*
                      * Original query has priority.
                      */

                      score +=
                        Math.max(
                          0,
                          100 -
                            sectionIndex * 20
                        );

                      /*
                      * Shopify position.
                      */

                      score +=
                        Math.max(
                          0,
                          10 -
                            productIndex
                        );

                      /*
                      * ==================================================
                      * COLOR ONLY SEARCH
                      * ==================================================
                      *
                      * Example:
                      *
                      * green
                      *
                      * Wire / connector / nut products
                      * get high priority.
                      *
                      * Cable ties get lower priority.
                      */

                      const colorWords = [
                        "green",
                        "yellow",
                        "blue",
                        "red",
                        "orange",
                        "purple",
                        "black",
                        "white",
                        "gray",
                        "grey",
                        "brown"
                      ];

                      const queryWords =
                        normalizedSearchTerm
                          .split(/\s+/)
                          .filter(Boolean);

                      const isColorOnlySearch =
                        queryWords.length === 1 &&
                        colorWords.includes(
                          queryWords[0]
                        );

                      if (isColorOnlySearch) {

                        const searchedColor =
                          queryWords[0];

                        /*
                        * ------------------------------------------
                        * EXACT COLOR
                        * ------------------------------------------
                        */

                        if (
                          productTitle.includes(
                            searchedColor
                          )
                        ) {
                          score += 1500;
                        }

                        if (
                          productText.includes(
                            searchedColor
                          )
                        ) {
                          score += 500;
                        }

                        /*
                        * ------------------------------------------
                        * WIRE RELATED
                        * ------------------------------------------
                        */

                        const wireTerms = [
                          "wire",
                          "grounding",
                          "ground",
                          "connector",
                          "connectors",
                          "wire nut",
                          "wire nuts",
                          "wire connector",
                          "wire connectors",
                          "grounding connector"
                        ];

                        const isWireRelated =
                          wireTerms.some(
                            (term) =>
                              productTitle.includes(
                                term
                              ) ||
                              productText.includes(
                                term
                              )
                          );

                        if (isWireRelated) {
                          score += 3000;
                        }

                        /*
                        * ------------------------------------------
                        * WIRE NUT
                        * ------------------------------------------
                        */

                        if (
                          productTitle.includes(
                            "wire nut"
                          ) ||
                          productText.includes(
                            "wire nut"
                          )
                        ) {
                          score += 3500;
                        }

                        /*
                        * ------------------------------------------
                        * WIRE CONNECTOR
                        * ------------------------------------------
                        */

                        if (
                          productTitle.includes(
                            "wire connector"
                          ) ||
                          productText.includes(
                            "wire connector"
                          )
                        ) {
                          score += 3200;
                        }

                        /*
                        * ------------------------------------------
                        * GROUNDING WIRE
                        * ------------------------------------------
                        */

                        if (
                          productTitle.includes(
                            "grounding wire"
                          ) ||
                          productText.includes(
                            "grounding wire"
                          ) ||
                          productTitle.includes(
                            "ground wire"
                          ) ||
                          productText.includes(
                            "ground wire"
                          )
                        ) {
                          score += 2800;
                        }

                        /*
                        * ------------------------------------------
                        * CABLE TIES
                        * ------------------------------------------
                        *
                        * Keep them, but move below
                        * wire products.
                        */

                        const cableTieTerms = [
                          "cable tie",
                          "cable ties",
                          "zip tie",
                          "zip ties",
                          "tie wrap",
                          "tie wraps"
                        ];

                        const isCableTie =
                          cableTieTerms.some(
                            (term) =>
                              productTitle.includes(
                                term
                              ) ||
                              productText.includes(
                                term
                              )
                          );

                        if (isCableTie) {
                          score -= 2500;
                        }

                        /*
                        * ------------------------------------------
                        * OTHER IRRELEVANT GREEN PRODUCTS
                        * ------------------------------------------
                        */

                        const lowerPriorityTerms = [
                          "sleeve",
                          "label",
                          "marker",
                          "tape",
                          "bag",
                          "clip",
                          "clamp"
                        ];

                        const isLowerPriorityProduct =
                          lowerPriorityTerms.some(
                            (term) =>
                              productTitle.includes(
                                term
                              ) ||
                              productText.includes(
                                term
                              )
                          );

                        if (
                          isLowerPriorityProduct
                        ) {
                          score -= 500;
                        }
                      }

                      /*
                      * ==================================================
                      * GREEN WIRE
                      * ==================================================
                      */

                      if (
                        normalizedSearchTerm.includes(
                          "green wire"
                        )
                      ) {

                        if (
                          productText.includes(
                            "green"
                          ) &&
                          productText.includes(
                            "wire"
                          )
                        ) {
                          score += 2000;
                        }

                        if (
                          productText.includes(
                            "green grounding wire"
                          ) ||
                          productText.includes(
                            "green ground wire"
                          )
                        ) {
                          score += 1500;
                        }

                        /*
                        * Product without wire should
                        * be pushed down.
                        */

                        if (
                          !productText.includes(
                            "wire"
                          )
                        ) {
                          score -= 1500;
                        }
                      }

                      /*
                      * ==================================================
                      * GREEN WIRE NUT
                      * ==================================================
                      */

                      if (
                        normalizedSearchTerm.includes(
                          "green wire nut"
                        )
                      ) {

                        const hasGreen =
                          productText.includes(
                            "green"
                          );

                        const hasWire =
                          productText.includes(
                            "wire"
                          );

                        const hasNutOrConnector =
                          productText.includes(
                            "nut"
                          ) ||
                          productText.includes(
                            "connector"
                          );

                        if (
                          hasGreen &&
                          hasWire &&
                          hasNutOrConnector
                        ) {
                          score += 3000;
                        }
                      }

                      /*
                      * ==================================================
                      * WIRE NUT
                      * ==================================================
                      */

                      if (
                        normalizedSearchTerm.includes(
                          "wire nut"
                        )
                      ) {

                        if (
                          productText.includes(
                            "wire nut"
                          ) ||
                          productText.includes(
                            "wire connector"
                          ) ||
                          productText.includes(
                            "wire cap"
                          )
                        ) {
                          score += 2500;
                        } else {
                          score -= 1500;
                        }
                      }

                      /*
                      * ==================================================
                      * WIRE CONNECTOR
                      * ==================================================
                      */

                      if (
                        normalizedSearchTerm.includes(
                          "wire connector"
                        )
                      ) {

                        if (
                          productText.includes(
                            "wire connector"
                          ) ||
                          productText.includes(
                            "wire nut"
                          ) ||
                          productText.includes(
                            "wire cap"
                          )
                        ) {
                          score += 2500;
                        } else {
                          score -= 1500;
                        }
                      }

                      /*
                      * ==================================================
                      * EXACT PHRASE
                      * ==================================================
                      */

                      if (
                        productText.includes(
                          normalizedSearchTerm
                        )
                      ) {
                        score += 700;
                      }

                      /*
                      * ==================================================
                      * ALL SEARCH WORDS MATCH
                      * ==================================================
                      */

                      const allWordsMatch =
                        queryWords.every(
                          (word) =>
                            productText.includes(
                              word
                            )
                        );

                      if (allWordsMatch) {
                        score += 500;
                      }

                      /*
                      * ==================================================
                      * SAVE PRODUCT
                      * ==================================================
                      */

                      const existing =
                        productMap.get(
                          productUrl
                        );

                      if (
                        !existing ||
                        score > existing.score
                      ) {

                        productMap.set(
                          productUrl,
                          {
                            item:
                              productItem.cloneNode(
                                true
                              ),

                            score: score
                          }
                        );
                      }
                    }
                  );
                }
              );

              /*
              * ==================================================
              * SORT FINAL PRODUCTS
              * ==================================================
              */

              const rankedProducts =
                [...productMap.values()]
                  .sort(
                    (a, b) =>
                      b.score - a.score
                  )
                  .slice(0, limit);

              /*
              * ==================================================
              * DEBUG
              * ==================================================
              */

              console.log(
                "FINAL RANKED PRODUCTS:",
                rankedProducts.map(
                  (product) => ({
                    score:
                      product.score,

                    title:
                      product.item.textContent
                        .replace(/\s+/g, " ")
                        .trim()
                  })
                )
              );

              /*
              * ==================================================
              * CLEAR OLD PRODUCTS
              * ==================================================
              */

              productContainer
                .querySelectorAll("li")
                .forEach((item) => {
                  item.remove();
                });

              /*
              * ==================================================
              * ADD FINAL PRODUCTS
              * ==================================================
              */

              rankedProducts.forEach(
                (product) => {

                  productContainer.appendChild(
                    product.item
                  );
                }
              );

              /*
              * ==================================================
              * UPDATE COUNT
              * ==================================================
              */

              const countElement =
                baseSection.querySelector(
                  "[data-predictive-search-live-region-count-value]"
                );

              if (countElement) {
                countElement.textContent =
                  rankedProducts.length;
              }

              /*
              * ==================================================
              * NO RESULTS
              * ==================================================
              */

              if (!rankedProducts.length) {

                console.log(
                  "NO RANKED PRODUCTS:",
                  normalizedSearchTerm
                );

                this.close();

                return;
              }

              /*
              * ==================================================
              * FINAL MARKUP
              * ==================================================
              */

              const resultsMarkup =
                baseSection.innerHTML;

              this.cachedResults[queryKey] =
                resultsMarkup;

              console.log(
                "FINAL RESULTS:",
                rankedProducts.length
              );

              this.renderSearchResults(
                resultsMarkup
              );
            })
            .catch((error) => {

              console.error(
                "Predictive search error:",
                error
              );

              this.close();
            });
  }


  
  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]").textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector(".section-header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    if (!this.isOpen) {
      const event = new CustomEvent("search:open");
      document.dispatchEvent(event);
    }

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute("aria-selected", false);
    }

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    if (this.isOpen) {
      const event = new CustomEvent("search:close");
      document.dispatchEvent(event);
    }

    this.isOpen = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);
